import { useCallback, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useCronogramaItensChecklistPagination } from "../hooks/CronogramaItensChecklist_cadastro/pagination";
import { DisciplinaItemChecklistLista } from "../../domain/models/items_disciplina_checklist_lista";
import { DisciplinaChecklistForm, DisciplinaChecklistSchema } from "../schemas/CronogramaItensChecklist_cadastro/checklist_item";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { DisciplinaChecklistLista } from "../../domain/models/disciplina_checklist_lista";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";

type LoadProjetoFuncState = {
  IdDisciplinaChecklist?: number;
  Page?: number | null;
  RowsPerPage?: number | null;
};
export const useDisciplinaItemsViewModel = () => {
  const {
    commons: {getAllLogAlteracaoUseCase, getAllLogAlteracaoByParentUseCase },
    projetos: { getAllDisciplinaItemsChecklistUseCase, updateDisciplinaItemsChecklistUseCase, getArqDisciplinaChecklistUseCase },
  } = useDependencyInjection();
  const { id, idProjeto } = useParams();
  const navigate = useNavigate();

  const pagination = useCronogramaItensChecklistPagination();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [idDisciplina, setIdDisciplina] = useState<number>();
  const location = useLocation();
  const [checklistLista, setChecklistLista] = useState<DisciplinaItemChecklistLista[]>([]);
  const [checklistListaItem, setChecklistListaItem] = useState<DisciplinaItemChecklistLista>();
  const [checklist, setChecklist] = useState<DisciplinaChecklistLista>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openDeleteArquivoModal, setOpenDeleteArquivoModal] = useState<boolean>(false);
  const [limparArquivo, setLimparArquivo] = useState<string>();
  const [arquivo, setArquivo] = useState<string>();
  const [isFinalizado, setIsFinalizado] = useState<boolean>(false);
  const [idProjetoNavigate, setIdProjetoNavigate] = useState<number>();
  const { pushErrorNotification, pushInfoNotification, pushWarnNotification } = usePageLoader();
  const changeHistory = useChangeHistory({
      getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
      getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
      onSetError: (e) => pushErrorNotification("Erro", [e]),
    });

  const hookForm = useForm<DisciplinaChecklistForm>({
    mode: "onSubmit",
    resolver: zodResolver(DisciplinaChecklistSchema),
    defaultValues: {
      idDisciplinaChecklist: undefined,
      status: 0,
      itens: [],
    },
  });

  //#region Lod
    const loadItensChecklist = useCallback(
    async (funcState?: LoadProjetoFuncState) => {
      try {
        const filterid = funcState?.IdDisciplinaChecklist ?? idDisciplina;
        const filterpage = funcState?.Page ?? pagination.page;
        const filterRowspage = funcState?.RowsPerPage ?? pagination.rowsPerPage;

        await getAllDisciplinaItemsChecklistUseCase.getAllDisciplinaItemsChecklist(
          filterid ?? idDisciplina ?? 0,
           filterpage,
           filterRowspage,
        );

        pagination.setRowsPerPage(getAllDisciplinaItemsChecklistUseCase.pageSize ?? 0);
        pagination.setTotalItems(getAllDisciplinaItemsChecklistUseCase.totalItems ?? 0);
        const fetchedItems = getAllDisciplinaItemsChecklistUseCase.items ?? [];
        const resumoInfo: any = fetchedItems.length > 0 ? fetchedItems[0].disciplinaChecklist : null;
        setChecklistLista(getAllDisciplinaItemsChecklistUseCase.items ?? []);
        const itensForm: any = (fetchedItems ?? []).map((item) => ({
          idDisciplinaItensChecklist: item.idDisciplinaItemChecklist,
          descricao: item.descricao,
          inspecao: item.inspecao,
          itens: item.itens,
        }));

        if (resumoInfo) {
          setChecklist(resumoInfo);
          setIsFinalizado(resumoInfo.status);
          hookForm.reset({
            idDisciplinaChecklist: resumoInfo.idDisciplinaChecklist ?? undefined,
            status: resumoInfo.status ?? 0,
            observacoes: resumoInfo.observacoes ?? "",
            normasTecnicas: resumoInfo.normasTecnicas ?? "",
            outrosItens: resumoInfo.outrosItens ?? "",

            idFuncionarioValidacao: resumoInfo.idFuncionarioValidacao ?? undefined,
            dataValidacao: resumoInfo.dataValidacao ? new Date(resumoInfo.dataValidacao) : undefined,

            idFuncionarioVerificacao: resumoInfo.idFuncionarioVerificacao ?? undefined,
            dataVerificacao: resumoInfo.dataVerificacao ? new Date(resumoInfo.dataVerificacao) : undefined,

            arquivo1: resumoInfo.arquivo1 ?? null,
            nomearquivo1: resumoInfo.nomearquivo1 ?? null,
            itens: itensForm,
          });
        }
      } catch (e: unknown) {
        setChecklistLista([]);
        throw new Error("Falha ao buscar itens do checklist!");
      }
    },
    [idDisciplina, pagination, getAllDisciplinaItemsChecklistUseCase, hookForm],
  );
  //#endregion

  //#region useEffect
  usePageStartBootstrap(async () => {
    try {
      if (!id || id.length === 0) {
        setErrorMessage("Falha ao recuperar id do checklist!");
        return;
      }
      setIdDisciplina(Number(id));
      setIdProjetoNavigate(Number(idProjeto));

      await loadItensChecklist({
        IdDisciplinaChecklist: Number(id),
      });
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados das páginas!")[0]);
    }
  });
  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      const jobs = [];
      if(id) {
        setIdDisciplina(Number(id));
        setIdProjetoNavigate(Number(idProjeto));
        jobs.push(await loadItensChecklist({
          IdDisciplinaChecklist: Number(id),
        }));
      }
      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [id, idProjeto, loadItensChecklist, pushErrorNotification]);
  //#endregion
  
  //#region  Evento
  const handleUpdateDisciplinaChecklist = useCallback(
    async (form: DisciplinaChecklistForm) => {
      try {
        setSaving(true);
        const temItemPendente = form.itens?.some(
        (item) => !item.inspecao || item.inspecao.trim() === ""
        );

      if (temItemPendente) {
        pushWarnNotification("Atenção", "Por favor, responda todos os itens do checklist antes de salvar!");
        return;
      }
      if(form.status === 1){
        if(!form.idFuncionarioValidacao || !form.dataValidacao){
          pushWarnNotification("Atenção", "Para finalizar o checklist, é necessário informar o responsável e a data de validação!");
          return;
        }
        if(!form.idFuncionarioVerificacao || !form.dataVerificacao){
          pushWarnNotification("Atenção", "Para finalizar o checklist, é necessário informar o responsável e a data de verificação!");
          return;
        }
      }

        await updateDisciplinaItemsChecklistUseCase.updateDisciplinaItemsChecklist({
          idDisciplinaChecklist: form.idDisciplinaChecklist ?? idDisciplina ?? 0,

          idChecklist: form.idChecklist ?? undefined,

          idFuncionarioValidacao: form.idFuncionarioValidacao ?? undefined,
          dataValidacao: form.dataValidacao ?? undefined,

          idFuncionarioVerificacao: form.idFuncionarioVerificacao ?? undefined,
          dataVerificacao: form.dataVerificacao ?? undefined,

          outrosItens: form.outrosItens ?? undefined,
          normasTecnicas: form.normasTecnicas ?? undefined,
          observacoes: form.observacoes ?? undefined,

          status: form.status ?? undefined,

          arquivo1: form.arquivo1 ?? "",
          nomearquivo1: form.nomearquivo1 ?? undefined,
          limparArquivo1: form.limparArquivo1 ?? false,

          itens:
            form.itens?.map((item) => ({
              idDisciplinaItensChecklist: item.idDisciplinaItensChecklist ?? 0,

              inspecao: item.inspecao ?? "NC",
              descricao: item.descricao ?? undefined,
              itens: item.itens ?? undefined,
            })) ?? [],
        });
        pushInfoNotification("Sucesso", "Salvo com sucesso");
        await loadItensChecklist();
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao atualizar checklist!"));
      } finally {
        setSaving(false);
      }
    },
    [updateDisciplinaItemsChecklistUseCase, idDisciplina, pushInfoNotification, loadItensChecklist, pushWarnNotification, pushErrorNotification],
  );



  const handleDownloadFile = useCallback(async (filename: string, IdDisciplinaChecklist: number) => {
    if (IdDisciplinaChecklist) {
      try {
        setLoading(true);

        await getArqDisciplinaChecklistUseCase.getArqDisciplinaChecklist(
          {idDisciplinaChecklist:Number(IdDisciplinaChecklist), nomearquivo1: filename},
        );
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao salvar inspeção obra!"));
      } finally {
        setLoading(false);
      }
    }
  }, [getArqDisciplinaChecklistUseCase, pushErrorNotification]);


  const handleDeleteFile = useCallback((campoLimpar: string, campoArquivoBase: string) => {
    if (!saving) {
    hookForm.setValue(campoLimpar as any, true);
    if (campoArquivoBase === "Arquivo1" || campoArquivoBase === "arquivo1") {
      hookForm.setValue('arquivo1', null); 
      hookForm.setValue('nomearquivo1', null); 
      hookForm.setValue("limparArquivo1", true, { shouldDirty: true, shouldValidate: true });
    }
    setChecklist(prev => {
      if (!prev) return undefined;
      return { 
        ...prev, 
        arquivo1: null, 
        nomeArquivo1: null
      };
    });
    }
  }, [saving, hookForm, setChecklist]);
    
 
 
  const handleOpenChangeHistory = useCallback(
    () => {
      changeHistory.handleOpenChangeHistory(Number(idDisciplina), LogTipoTabela.DISCIPLINA_CHECKLIST, true);
    },
    [changeHistory, idDisciplina],
  );

  const handleProjetoCadastro = useCallback(
    () => {
        navigate(`/Projetos_cadastro/${idProjetoNavigate}/2`);
    },
    [idProjetoNavigate, navigate],
  );

  //#endregion

  return {
    checklistListaItem,
    pagination,
    loading,
    saving,
    checklistLista,
    hookForm,
    arquivo,
    limparArquivo,
    openDeleteArquivoModal,
    checklist,
    isFinalizado,
    changeHistory,
    setIsFinalizado,
    handleUpdateDisciplinaChecklist,
    handleOpenChangeHistory,
    handleReload,
    handleDownloadFile,
    handleProjetoCadastro,
    handleDeleteFile
  };
};
